import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { Form, Row, Col, Select, DatePicker, Input, Switch, Space, Spin } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { useWatch } from "antd/es/form/Form"
import {
  fetchExperienceById,
  fetchExperienceBookingDetails,
  resetExperienceBookingData,
  resetTripAvailableSlots
} from "store/experiences"
import TimeDurationSelector from "./TimeDurationSelector"
import GuestInputGroup from "./GuestInputGroup"
import GuestCountSection from "./GuestCountSection"
import { calculateBookingTotal } from "../helpers/calculateBookingTotal"
import { hasNoUserBookingChanges, resolveEditPaymentType } from "../bookingUtils"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"
import { getCardFeeFraction, isBalanceInCash } from "utils/bookingCalculations"

const { Option } = Select
const { TextArea } = Input

const SpinningPanel = ({ panel, loading }) => <Spin spinning={loading}>{panel}</Spin>

SpinningPanel.propTypes = {
  panel: PropTypes.node,
  loading: PropTypes.bool
}

const ExperienceDetail = ({
  form, bookingId, booking, isEdit, mode, filterExperienceIds,
  cardFeeInfo, appliedPromos = [],
  bookingSelections, updateBookingSelections
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [durationLoading, setDurationLoading] = useState(false)
  const [timeLoading, setTimeLoading] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const lastSlotsFetchRef = useRef(null)

  const {
    list: experiences,
    loading: experiencesListLoading,
    experienceById: { experienceDetail, loading: experienceLoading },
    experienceBookingData
  } = useSelector((state) => state.experiences)

  const isPrivateExperience = !!experienceDetail?.duration_type
  const privateDurationType = experienceDetail?.duration_type
  // Duration/time-slot selection doesn't apply to multi_day. Only render those fields once the
  // experience detail has actually loaded — otherwise (e.g. while editing a multi_day booking whose
  // API still returns a duration/time) they flash with stale "0 hrs"/slot values before the detail
  // resolves and the multi_day type is known.
  const durationTimeApplicable = !experienceLoading && !!experienceDetail && privateDurationType !== "multi_day"

  const baseFilteredExperiences =
    filterExperienceIds && filterExperienceIds.length > 0
      ? experiences.filter((exp) => filterExperienceIds.includes(exp.id))
      : experiences

  const displayedExperiences = baseFilteredExperiences

  const { scheduled_deletion_date } = useSelector((state) => state.user?.guide) || {}
  const accessibilityRequired = useWatch("accessibility_required", form)
  const selectedExperience = useWatch("experience", form)
  const paymentType = useWatch("paymentType", form)
  const depositOnly = useWatch("depositOnly", form)
  const depositPercent = useWatch("depositPercent", form)
  const customDepositAmount = useWatch("customDepositAmount", form)
  const paymentOption = useWatch("paymentOption", form)
  const depositFraction = getCardFeeFraction({ depositOnly, depositPercent, customDepositAmount, balanceInCash: isBalanceInCash(paymentOption) })
  const date = useWatch("date", form)
  const selectedExperienceId = selectedExperience?.value

  const effectivePaymentType = resolveEditPaymentType(paymentType, booking, isEdit)

  // ── Regular-experience slot/duration data ──────────────────────────────────
  const slotData = experienceBookingData?.bookingData?.data
  const timeSlots = (Array.isArray(slotData) ? slotData : []).map((item) => {
    const slot = typeof item === "object" ? item.slot : item
    return { key: { slot, available: item?.available !== false }, value: slot }
  })
  const baseDurations = useMemo(() => {
    const durations = date && experienceBookingData?.bookingData?.trip_durations
    return Array.isArray(durations) ? durations : []
  }, [date, experienceBookingData?.bookingData?.trip_durations])
  const selectedTripLength = bookingSelections?.tripLength
  const isTripLengthIncluded = selectedTripLength ? baseDurations.some((slot) => slot.key === selectedTripLength.key) : true

  // Resolve the trip price for the currently selected duration so guest-type flags (adults_allowed,
  // children_allowed, etc.) always reflect the chosen duration, not just the first price.
  const selectedTripPrice = useMemo(() => {
    const fromTripLength = bookingSelections?.tripLength?.price
    if (fromTripLength) return fromTripLength
    const key = bookingSelections?.tripLength?.key
    if (key && experienceDetail?.trip_prices?.length) {
      return experienceDetail.trip_prices.find((p) => p.id === key) ?? experienceDetail.trip_prices[0]
    }
    return experienceDetail?.trip_prices?.[0] ?? null
  }, [bookingSelections?.tripLength, experienceDetail?.trip_prices])
  const updatedDurationSlots = useMemo(
    () => (isEdit && !isTripLengthIncluded ? [...baseDurations, selectedTripLength] : baseDurations),
    [isEdit, isTripLengthIncluded, baseDurations, selectedTripLength]
  )

  useEffect(() => {
    if (updatedDurationSlots.length === 1) {
      const onlySlot = updatedDurationSlots[0]
      form.setFieldsValue({ duration: onlySlot.value })
      updateBookingSelections({ tripLength: onlySlot })
    }
  }, [updatedDurationSlots, form, updateBookingSelections])

  const existingTimeSlotValue = `${booking?.start_time} - ${booking?.end_time}`
  const isExistingSlotPresent = timeSlots.some((slot) => slot.value === existingTimeSlotValue)
  const shouldAddTimeSlot =
    isEdit &&
    existingTimeSlotValue &&
    !isExistingSlotPresent &&
    selectedTripLength?.value === booking?.selected_trip_duration &&
    bookingSelections?.date === booking?.booked_date

  const updatedTimeSlots = useMemo(
    () =>
      shouldAddTimeSlot
        ? [...timeSlots, { key: existingTimeSlotValue, value: existingTimeSlotValue }]
        : timeSlots,
    [shouldAddTimeSlot, timeSlots, existingTimeSlotValue]
  )

  useEffect(() => {
    if (!timeLoading && updatedTimeSlots.length === 1 && !bookingSelections?.timeSlot) {
      const onlySlot = updatedTimeSlots[0]
      form.setFieldsValue({ time: onlySlot.value })
      updateBookingSelections({ timeSlot: onlySlot.key?.slot || onlySlot.key })
    }
  }, [updatedTimeSlots, timeLoading, bookingSelections?.timeSlot, form, updateBookingSelections])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleExperienceChange = (experience) => {
    form.setFieldsValue({ experience, date: "", main: { adults: 0, children: 0, infants: 0, passengers: 0 } })
    updateBookingSelections({ date: null, timeSlot: null, tripLength: null, counts: null, amount: 0, baseAmount: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 })
    dispatch(resetExperienceBookingData())
    dispatch(resetTripAvailableSlots())
    setDatePickerOpen(true)
  }

  const disableDates = useCallback(
    (current) => {
      const date = dayjs(current)
      if (date.isBefore(dayjs(), "day")) return false
      const weekdayMap = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6
      }
      const blockedWeekdays = experienceDetail?.unavailable_weekdays?.map((day) => weekdayMap[day.toLowerCase()]) || []
      const isSoldOut = experienceDetail?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const isUnavailable = experienceDetail?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const hasDateRanges = Array.isArray(experienceDetail?.available_date_ranges) && experienceDetail.available_date_ranges.length > 0
      let isInAvailableRange = true
      if (hasDateRanges) {
        isInAvailableRange = experienceDetail?.available_date_ranges.some((range) => {
          const start = dayjs(range.start).startOf("day")
          const end = dayjs(range.end).endOf("day")
          return date.isBetween(start, end, null, "[]")
        })
      }
      return (
        date.isAfter(scheduled_deletion_date) ||
        blockedWeekdays.includes(date.day()) ||
        isUnavailable ||
        isSoldOut ||
        (hasDateRanges && !isInAvailableRange)
      )
    },
    [experienceDetail, scheduled_deletion_date]
  )

  const handleDateChange = useCallback(
    (value) => {
      setDatePickerOpen(false)
      const formattedDate = dayjs(value).format("YYYY-MM-DD")
      // multi_day has no duration step, so keep the tripLength seeded from trip_prices[0]
      // (nulling it would wipe the pricing data calculateBookingTotal needs). Regular and
      // single_day flows re-select tripLength via the duration step, so reset it there.
      const isMultiDay = privateDurationType === "multi_day"
      const tripPrice = experienceDetail?.trip_prices?.[0]
      updateBookingSelections({
        date: formattedDate,
        timeSlot: null,
        tripLength: isMultiDay && tripPrice ? { key: tripPrice.id, value: "", price: tripPrice } : null
      })
      form.setFieldsValue({ date: value, duration: null, time: null })
    },
    [updateBookingSelections, form, privateDurationType, experienceDetail?.trip_prices]
  )

  const handleTripDurationChange = useCallback(
    (tripLength) => {
      const isTripDurationUnchanged = tripLength?.value === booking?.selected_trip_duration
      setTimeLoading(true)

      // Embed the matching trip price so guest-type flags and calculateBookingTotal have pricing data.
      const matchingPrice = experienceDetail?.trip_prices?.find((p) => p.id === tripLength.key) ?? experienceDetail?.trip_prices?.[0]
      const enrichedTripLength = matchingPrice ? { ...tripLength, price: matchingPrice } : tripLength

      const updatedSelections = {
        tripLength: enrichedTripLength,
        timeSlot: isTripDurationUnchanged ? existingTimeSlotValue : null
      }

      if (isTripDurationUnchanged) {
        // Editing an existing booking with its original duration — keep guest counts and reprice.
        const counts = bookingSelections?.counts
        const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
        const hasGuestCounts =
          counts &&
          (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
        if (hasGuestCounts) {
          const tempSelections = { ...bookingSelections, tripLength: enrichedTripLength }
          const { total, guestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(tempSelections, counts, guideFeePercent, {
            paymentType: effectivePaymentType,
            isEdit,
            originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
            customFees: experienceDetail?.assigned_custom_fees,
            platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
            cardFeeInfo,
            originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
            discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
            dayAdjustments: experienceDetail?.day_adjustments,
            depositFraction
          }) || { total: 0, guestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
          updatedSelections.amount = total
          updatedSelections.baseAmount = guestSubtotal
          updatedSelections.breakdown = breakdown
          updatedSelections.preCardTotal = preCardTotal
          updatedSelections.cardProcessingFee = cardProcessingFee
        }
      } else {
        // Duration changed — reset guest fields so the new duration's allowed types take effect.
        form.setFieldsValue({ main: { adults: 0, children: 0, infants: 0, passengers: 0 } })
        updatedSelections.counts = null
        updatedSelections.amount = 0
        updatedSelections.baseAmount = 0
        updatedSelections.breakdown = []
        updatedSelections.preCardTotal = 0
        updatedSelections.cardProcessingFee = 0
      }

      updateBookingSelections(updatedSelections)
      form.setFieldsValue({
        duration: enrichedTripLength?.value,
        time: isTripDurationUnchanged ? existingTimeSlotValue : null
      })
    },
    [
      updateBookingSelections, bookingSelections, existingTimeSlotValue,
      experienceDetail?.guide_booking_fee_percentage, experienceDetail?.assigned_custom_fees,
      experienceDetail?.platform_fee_setting, experienceDetail?.day_adjustments,
      experienceDetail?.trip_prices, booking, effectivePaymentType, isEdit,
      cardFeeInfo, appliedPromos, depositFraction, form
    ]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      form.setFieldsValue({ time: value })
      updateBookingSelections({ timeSlot: value })
    },
    [updateBookingSelections, form]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, guestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(bookingSelections, newCounts, guideFeePercent, {
        paymentType: effectivePaymentType,
        isEdit,
        originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
        customFees: experienceDetail?.assigned_custom_fees,
        platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
        cardFeeInfo,
        originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
        discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
        dayAdjustments: experienceDetail?.day_adjustments,
        depositFraction
      }) || { total: 0, guestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

      updateBookingSelections({
        counts: newCounts,
        amount: total,
        baseAmount: guestSubtotal,
        preCardTotal,
        cardProcessingFee,
        breakdown
      })
    },
    [updateBookingSelections, bookingSelections, booking, experienceDetail?.guide_booking_fee_percentage, effectivePaymentType, isEdit, experienceDetail?.assigned_custom_fees, experienceDetail?.platform_fee_setting, experienceDetail?.day_adjustments, cardFeeInfo, appliedPromos, depositFraction]
  )

  // For private experiences, GuestCountSection stores values in the form — sync them to bookingSelections
  const handlePrivateGuestChange = useCallback(
    (mainCounts) => {
      handleGuestsChange({ main: mainCounts, extra: bookingSelections?.counts?.extra || {} })
    },
    [handleGuestsChange, bookingSelections?.counts?.extra]
  )

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEdit && !experienceDetail?.id) return
    if (isEdit && hasNoUserBookingChanges(bookingSelections, booking)) return
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const currentAmount = bookingSelections?.amount
    const hasGuestCounts =
      counts &&
      (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (hasGuestCounts && tripLength) {
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, guestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(bookingSelections, counts, guideFeePercent, {
        paymentType: effectivePaymentType,
        isEdit,
        originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
        customFees: experienceDetail?.assigned_custom_fees,
        platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
        cardFeeInfo,
        originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
        discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit),
        dayAdjustments: experienceDetail?.day_adjustments,
        depositFraction
      }) || { total: 0, guestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

      // Only update if amount is different (to avoid infinite loops)
      if (total !== currentAmount) {
        updateBookingSelections({
          amount: total,
          baseAmount: guestSubtotal,
          breakdown,
          preCardTotal,
          cardProcessingFee
        })
      }
    }
  }, [
    experienceDetail?.id, experienceDetail?.guide_booking_fee_percentage, experienceDetail?.assigned_custom_fees,
    experienceDetail?.platform_fee_setting, experienceDetail?.day_adjustments, booking, booking?.booking_fee_percentage,
    booking?.payment_summary?.tax_and_fee, booking?.platform_fee_setting,
    effectivePaymentType, bookingSelections?.counts, bookingSelections?.tripLength,
    bookingSelections?.amount, isEdit, updateBookingSelections, bookingSelections, cardFeeInfo, appliedPromos, depositFraction
  ])

  useEffect(() => {
    if (isEdit && booking?.trip?.uuid) {
      dispatch(fetchExperienceById(booking.trip.uuid, "", mode))
    }
  }, [isEdit, booking?.trip?.uuid, dispatch, mode])

  useEffect(() => {
    if (!isEdit && selectedExperience?.value) {
      dispatch(fetchExperienceById(selectedExperience.value, "", mode))
    }
  }, [selectedExperience?.value, isEdit, dispatch, mode])

  // For multi_day private experiences there is no duration-selection step, so tripLength is never
  // set by the user. Seed it from trip_prices[0] as soon as the experience detail loads so that
  // calculateBookingTotal has the pricing data (flat_rate, price_per_person, etc.) it needs.
  useEffect(() => {
    if (privateDurationType !== "multi_day") return
    const tripPrice = experienceDetail?.trip_prices?.[0]
    if (!tripPrice) return
    updateBookingSelections({ tripLength: { key: tripPrice.id, value: "", price: tripPrice } })
  }, [experienceDetail?.id, privateDurationType, updateBookingSelections])

  // Set durationLoading whenever date changes for any experience that has durations
  useEffect(() => {
    if (date && privateDurationType !== "multi_day") {
      setDurationLoading(true)
    }
  }, [date, privateDurationType])

  // Fetch duration data when a date is picked (regular + single_day private)
  useEffect(() => {
    if (privateDurationType === "multi_day") return
    if (date && selectedExperienceId) {
      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "trip_data", "", bookingSelections?.date, bookingId, mode))
        .then(() => setDurationLoading(false))
        .catch(() => setDurationLoading(false))
    }
  }, [date, selectedExperienceId, dispatch, mode, bookingId, privateDurationType, bookingSelections?.date])

  // Fetch available time slots when duration + date are set (regular + single_day private)
  useEffect(() => {
    if (privateDurationType === "multi_day") return
    const tripLength = bookingSelections?.tripLength?.price?.id
    const tripDate = bookingSelections?.date
    const callKey = `${selectedExperienceId}-${tripLength}-${tripDate}-${bookingId}`
    if (tripLength && tripDate && selectedExperienceId) {
      if (lastSlotsFetchRef.current === callKey) return
      lastSlotsFetchRef.current = callKey
      setTimeLoading(true)
      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "available_slots", tripLength, tripDate, bookingId, mode))
        .then(() => setTimeLoading(false))
        .catch((error) => {
          console.error("Failed to fetch available slots:", error)
          setTimeLoading(false)
          updateBookingSelections({ timeSlot: null })
          form.setFieldsValue({ time: null })
        })
    } else {
      setTimeLoading(false)
      lastSlotsFetchRef.current = null
    }
  }, [bookingSelections?.tripLength?.price?.id, bookingSelections?.date, selectedExperienceId, dispatch, mode, bookingId, form, privateDurationType, updateBookingSelections])

  // ── Derived visibility flags ───────────────────────────────────────────────

  const showPrivateGuestSection =
    isPrivateExperience &&
    ((privateDurationType === "single_day" && !!bookingSelections?.timeSlot) ||
      (privateDurationType === "multi_day" && !!bookingSelections?.date))

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Row gutter={16}>

      {/* ── Experience dropdown ─────────────────────────────────────────── */}
      <Col xs={24} sm={24}>
        <Form.Item
          name="experience"
          label={t("booking.labels.experience")}
          rules={[{ required: true, message: t("booking.error_messages.experience_required") }]}
        >
          <Select
            labelInValue
            placeholder="Select Experience"
            showSearch
            onChange={handleExperienceChange}
            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            disabled={isEdit}
            loading={experiencesListLoading}
          >
            {displayedExperiences?.map((trip) => (
              <Option key={trip.id} value={trip.uuid} label={trip.title}>
                {trip.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      {/* ── Single date picker for all experience types ──────────────────── */}
      {(isEdit || selectedExperience?.value) && (
        <Col xs={24} sm={24}>
          <Form.Item
            name="date"
            label={t("booking.labels.booked_date")}
            rules={[{ required: true, message: t("booking.error_messages.date_required") }]}
          >
            <DatePicker
              value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
              style={{ width: "100%" }}
              disabledDate={experienceLoading ? () => true : disableDates}
              onChange={handleDateChange}
              format="MM/DD/YYYY"
              open={datePickerOpen}
              onOpenChange={setDatePickerOpen}
              panelRender={(panel) => <SpinningPanel panel={panel} loading={experienceLoading} />}
            />
          </Form.Item>
        </Col>
      )}

      {/* ── Duration — regular + single_day private ─────────────────────── */}
      {durationTimeApplicable && (isEdit || (selectedExperience?.value && date)) && (
        <Col xs={24} sm={24}>
          <Form.Item
            label={t("booking.labels.duration")}
            name="duration"
            rules={[{ required: true, message: t("booking.error_messages.duration_required") }]}
          >
            {durationLoading ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <Spin size="small" />
              </div>
            ) : (
              <TimeDurationSelector
                options={updatedDurationSlots || []}
                selectedValue={bookingSelections?.tripLength?.key}
                onChange={handleTripDurationChange}
              />
            )}
          </Form.Item>
        </Col>
      )}

      {/* ── Time slot — regular + single_day private ────────────────────── */}
      {durationTimeApplicable && bookingSelections?.tripLength && (
        <Col xs={24} sm={24}>
          <Form.Item
            label={t("booking.labels.booked_time")}
            name="time"
            rules={[{ required: true, message: t("booking.error_messages.time_required") }]}
          >
            {timeLoading ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <Spin size="small" />
              </div>
            ) : (
              <TimeDurationSelector
                options={updatedTimeSlots}
                selectedValue={bookingSelections?.timeSlot}
                onChange={(selected) => handleTimeSlotChange(selected.key?.slot || selected.key)}
              />
            )}
          </Form.Item>
        </Col>
      )}

      {/* ── Guest input for regular experiences ─────────────────────────── */}
      {!isPrivateExperience && bookingSelections?.timeSlot && (
        <Col xs={24} sm={24}>
          <GuestInputGroup
            form={form}
            setCounts={handleGuestsChange}
            booking={booking}
            bookingSelections={bookingSelections}
          />
        </Col>
      )}

      {/* ── Guest count for private experiences ─────────────────────────── */}
      {showPrivateGuestSection && (
        <Col xs={24} sm={24}>
          <GuestCountSection
            form={form}
            tripPrice={selectedTripPrice}
            onChange={handlePrivateGuestChange}
            minGuests={experienceDetail?.min_guests}
            maxGuests={experienceDetail?.max_guests}
          />
        </Col>
      )}

      {/* ── Common fields ────────────────────────────────────────────────── */}
      <Col xs={24} sm={24}>
        <Form.Item name="accessibility_required" valuePropName="checked">
          <Space align="center">
            <Switch value={accessibilityRequired} onChange={(val) => form.setFieldsValue({ accessibility_required: val })} />
            <span>{t("booking.labels.accessibility_service")}</span>
          </Space>
        </Form.Item>
      </Col>
      <Col xs={24} sm={24}>
        {accessibilityRequired && (
          <Form.Item
            name="accessibility_notes"
            rules={[{ required: accessibilityRequired, message: t("booking.error_messages.accessibility_notes_required") }]}
          >
            <TextArea placeholder={t("booking.labels.accessibility_notes")} rows={3} />
          </Form.Item>
        )}
      </Col>
      <Col xs={24} sm={24}>
        <Form.Item label={t("booking.labels.notes")} name="notes">
          <TextArea rows={3} />
        </Form.Item>
      </Col>
    </Row>
  )
}

ExperienceDetail.propTypes = {
  form: PropTypes.object.isRequired,
  bookingId: PropTypes.string.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  mode: PropTypes.string,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number),
  cardFeeInfo: PropTypes.shape({ rate: PropTypes.number, fixed: PropTypes.number }),
  appliedPromos: PropTypes.array,
  bookingSelections: PropTypes.object.isRequired,
  updateBookingSelections: PropTypes.func.isRequired
}

export default ExperienceDetail
